import type { MarketingTranslator } from '@/i18n/marketing'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { clsx } from 'clsx'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'

import { CtaButton } from './CtaButton'
import {
  InquiryDialog,
  Field,
  CheckCircle,
  inputClass,
  RegionPicker,
  ConsentSection,
} from './inquiry'
import { getFranchiseTypes } from '@/pages/home/briefing.content'
import { lcMarketingApi } from '@/api/lcMarketing'
import { trackNaverConversion } from '@/lib/naver'

const phoneRegex = /^(01[016789])([1-9]{1}[0-9]{2,3})([0-9]{4})$/

function createFormSchema(mt: MarketingTranslator) {
  const FormSchema = z
    .object({
      name: z.string().min(1, mt('form.nameRequired')).max(50, mt('form.maxName')),
      phone: z.string().regex(phoneRegex, mt('form.phoneInvalid')),
      franchiseTypes: z
        .array(z.enum(['academy', 'tutoring']))
        .min(1, mt('form.franchiseTypeRequired')),
      sido: z.string().optional(),
      sigungu: z.string().optional(),
      inquiryText: z.string().min(1, mt('form.inquiryRequired')).max(1000, mt('form.maxInquiry')),
      privacyConsent: z.literal(true, {
        errorMap: () => ({ message: mt('form.privacyRequired') }),
      }),
      marketingConsent: z.boolean(),
    })
    .refine(
      (d) => (!d.sido && !d.sigungu) || (!!d.sido && !!d.sigungu),
      { path: ['sigungu'], message: mt('form.districtAlsoRequired') },
    )
  return FormSchema
}

type FormValues = z.infer<ReturnType<typeof createFormSchema>>

interface FranchiseInquiryModalProps {
  open: boolean
  onClose: () => void
}

export function FranchiseInquiryModal({
  open,
  onClose,
}: FranchiseInquiryModalProps) {
  const { t: mt, i18n } = useTranslation('marketing')
  const FRANCHISE_TYPES = getFranchiseTypes(mt)
  const FormSchema = createFormSchema(mt)

  const [submitted, setSubmitted] = useState(false)

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      phone: '',
      franchiseTypes: [],
      sido: '',
      sigungu: '',
      inquiryText: '',
      privacyConsent: false as unknown as true,
      marketingConsent: false,
    },
  })

  const submitMutation = useMutation({
    mutationFn: (values: FormValues) =>
      lcMarketingApi.submitFranchiseInquiry({
        name: values.name.trim(),
        phone: values.phone.trim(),
        franchiseTypes: values.franchiseTypes,
        sido: values.sido?.trim() || undefined,
        sigungu: values.sigungu?.trim() || undefined,
        inquiryText: values.inquiryText.trim(),
        privacyConsent: true,
        marketingConsent: values.marketingConsent,
      }),
    onSuccess: () => {
      setSubmitted(true)
      trackNaverConversion('custom004')
      setTimeout(() => {
        handleClose()
      }, 1800)
    },
    onError: () => {
      toast.error(mt('form.inquiryFailed'))
    },
  })

  const handleClose = () => {
    reset()
    setSubmitted(false)
    onClose()
  }

  const footer = !submitted ? (
    <CtaButton
      id="franchise-form-submit"
      type="submit"
      form="franchise-form"
      variant="primary"
      size="lg"
      fullWidth
      disabled={!isValid || submitMutation.isPending}
    >
      {submitMutation.isPending ? mt('form.sending') : mt('franchise.franchiseContent.earlyBird.ctaLabel')}
    </CtaButton>
  ) : null

  return (
    <InquiryDialog
      open={open}
      onClose={handleClose}
      title={mt('form.inquiryTitle')}
      footer={footer}
    >
      {submitted ? (
        <div className="py-12 text-center text-qb-body-2 text-qb-text-high">
          {mt('form.inquiryReceived')}
        </div>
      ) : (
        <form
          id="franchise-form"
          onSubmit={handleSubmit((v) => submitMutation.mutate(v))}
          className="flex flex-col gap-6 px-8 pt-6 pb-5"
        >
          {i18n.resolvedLanguage === 'en' && (
            <p className="text-sm text-qb-text-mid">{mt('domesticFormNotice')}</p>
          )}
          <Field label={mt('form.name')} error={errors.name?.message}>
            <input
              {...register('name')}
              placeholder={mt('form.nameRequired')}
              className={inputClass}
              autoComplete="name"
            />
          </Field>

          <Field label={mt('form.phone')} error={errors.phone?.message}>
            <input
              {...register('phone')}
              placeholder="01012345678"
              className={inputClass}
              inputMode="tel"
              autoComplete="tel"
            />
          </Field>

          <Field
            label={mt('form.franchiseType')}
            error={(errors.franchiseTypes as { message?: string } | undefined)?.message}
          >
            <Controller
              control={control}
              name="franchiseTypes"
              render={({ field }) => (
                <div className="flex gap-[10px]">
                  {FRANCHISE_TYPES.map((opt) => {
                    const checked = field.value.includes(opt.value)
                    return (
                      <button
                        type="button"
                        key={opt.value}
                        onClick={() => {
                          if (checked) {
                            field.onChange(field.value.filter((v) => v !== opt.value))
                          } else {
                            field.onChange([...field.value, opt.value])
                          }
                        }}
                        className={clsx(
                          'flex items-center gap-[10px] h-[51px] px-4 rounded-[12px] border text-[16px] font-medium leading-[1.5] transition-colors flex-1',
                          checked
                            ? 'bg-qb-primary-subtle border-qb-primary text-qb-text-high'
                            : 'bg-qb-bg-secondary border-transparent text-qb-text-mid',
                        )}
                      >
                        <CheckCircle checked={checked} />
                        {opt.label}
                      </button>
                    )
                  })}
                </div>
              )}
            />
          </Field>

          <Field label={mt('form.location')} error={errors.sigungu?.message}>
            <Controller
              control={control}
              name="sido"
              render={({ field: sidoField }) => (
                <Controller
                  control={control}
                  name="sigungu"
                  render={({ field: sigunguField }) => (
                    <RegionPicker
                      sido={sidoField.value || undefined}
                      sigungu={sigunguField.value || undefined}
                      onChange={(next) => {
                        sidoField.onChange(next.sido ?? '')
                        sigunguField.onChange(next.sigungu ?? '')
                      }}
                    />
                  )}
                />
              )}
            />
          </Field>

          <Field label={mt('form.inquiry')} error={errors.inquiryText?.message}>
            <textarea
              {...register('inquiryText')}
              placeholder={mt('form.questionsPlaceholder')}
              rows={4}
              className={clsx(inputClass, 'py-3 h-auto resize-y min-h-[48px]')}
            />
          </Field>

          <Controller
            control={control}
            name="privacyConsent"
            render={({ field: privacy }) => (
              <Controller
                control={control}
                name="marketingConsent"
                render={({ field: marketing }) => (
                  <ConsentSection
                    privacyAgreed={Boolean(privacy.value)}
                    marketingAgreed={marketing.value}
                    onPrivacyChange={(next) => privacy.onChange(next as unknown as true)}
                    onMarketingChange={(next) => marketing.onChange(next)}
                  />
                )}
              />
            )}
          />
        </form>
      )}
    </InquiryDialog>
  )
}
